import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandProvider } from "./context/BrandContext";
import { OrgProvider } from "./context/OrgContext";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Pipeline from "./pages/Pipeline";
import Settings from "./pages/Settings";
import UserGuide from "./pages/UserGuide";
import CustomProperties from "./pages/CustomProperties";
import AutomationsHub from "./pages/AutomationsHub";
import Inbox from "./pages/Inbox";
import AcceptInvite from "./pages/AcceptInvite";
import Analytics from "./pages/Analytics";
import Companies from "./pages/Companies";
import CareDirectory from "./pages/CareDirectory";
import DemoLauncher from "./pages/DemoLauncher";
import DemoTour from "./components/DemoTour";
import MarketIntel from "./pages/MarketIntel";
import Events from "./pages/Events";
import PublicLayout from "./pages/public/PublicLayout";
import PublicHome from "./pages/public/PublicHome";
import Browse from "./pages/public/Browse";
import SupplierPage from "./pages/public/SupplierPage";
import { HowToChoose, ForSuppliers, Advisor } from "./pages/public/LandingPages";
import { HomecareWebinar, HomecareQuestionnaire } from "./pages/public/HomecareFunnel";
import { InsuranceValue, ProcurementFunnel, ProcurementHub } from "./pages/public/ProcurementFunnels";
import { ProcurementReportForm, ProcurementReportSuccess, ProcurementReportView } from "./pages/public/ProcurementReport";
import ProcurementReports from "./pages/ProcurementReports";
import SupplierTrust from "./pages/SupplierTrust";
import ListingProposals from "./pages/ListingProposals";
import Governance from "./pages/Governance";
import QuestionSets from "./pages/QuestionSets";
import Fulfilment from "./pages/Fulfilment";
import Partners from "./pages/Partners";
import VendorSubscriptions from "./pages/VendorSubscriptions";
import { AssuranceProgramme, AssuranceSuccess, AssuranceEvidence } from "./pages/public/AssuranceProgramme";
import GetStarted from "./pages/public/GetStarted";
import Compare from "./pages/public/Compare";
import ProductPage from "./pages/public/ProductPage";
import Pricing from "./pages/public/Pricing";
import CreateListing from "./pages/public/CreateListing";
import CreateListingPay from "./pages/public/CreateListingPay";
import DiscountCodes from "./pages/DiscountCodes";
import ReviewRegisterAdmin from "./pages/ReviewRegisterAdmin";
import ReviewRegister from "./pages/public/ReviewRegister";
import { About, Contact } from "./pages/public/InfoPages";
import { PrivacyPolicy, TermsConditions, AcceptableUse, TermsOfBusiness } from "./pages/public/LegalPages";
import { ForGovernment, ForMembershipOrgs, ForIndustrySpecialists, ForCareProviders, ForCareServices, CommonProblems } from "./pages/public/AudiencePages";
import { KnowledgeHub, ArticlePage } from "./pages/public/KnowledgeHub";
import Podcast, { PodcastPost } from "./pages/public/Podcast";
import Webinars from "./pages/public/Webinars";
import WebinarWatch from "./pages/public/WebinarWatch";
import BookACall from "./pages/public/BookACall";
import { ClaimListing, ClaimSuccess, SetPassword, SupplierPortal } from "./pages/public/SupplierClaim";
import ListingApprovals from "./pages/ListingApprovals";
import ProductReviews from "./pages/ProductReviews";
import SupplierInvites from "./pages/SupplierInvites";
import SupplierEditor from "./pages/SupplierEditor";
import SupplierVerification from "./pages/SupplierVerification";
import BulkEmail from "./pages/BulkEmail";
import AssurancePipeline from "./pages/AssurancePipeline";
import FunnelInbox from "./pages/FunnelInbox";
import Ops from "./pages/Ops";
import VerificationAssurance from "./pages/VerificationAssurance";
import CuratedLists from "./pages/CuratedLists";
import Contacts from "./pages/Contacts";
import Lists from "./pages/Lists";
import Forms from "./pages/Forms";
import PublicForm from "./pages/PublicForm";
import Meetings from "./pages/Meetings";
import PublicBooking from "./pages/PublicBooking";
import Campaigns from "./pages/Campaigns";
import CampaignBuilder from "./pages/CampaignBuilder";
import SupplierLogin from "./pages/SupplierLogin";
import PublicUnsubscribe from "./pages/PublicUnsubscribe";
import BuyingHelp from "./pages/public/BuyingHelp";
import BuyingHelpSurvey from "./pages/public/BuyingHelpSurvey";
import PreferenceCenter from "./pages/PreferenceCenter";
import Layout from "./components/Layout";
import "./App.css";

const isSupplier = (user) => user?.role === 'supplier';
const homeForUser = (user) => (isSupplier(user) ? '/portal' : '/admin');

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Suppliers may only use their listing portal, never the CRM.
  if (isSupplier(user)) {
    return <Navigate to="/portal" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={homeForUser(user)} replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
      <OrgProvider>
      <BrowserRouter>
        <DemoTour />
        <Toaster 
          position="top-right" 
          theme="light"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: '1px solid #E5E7EB',
              color: '#111827',
            },
          }}
        />
        <Routes>
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } />
          <Route path="/reset-password" element={
            <ResetPassword />
          } />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/demo" element={<DemoLauncher />} />
          <Route path="/supplier-login" element={<SupplierLogin />} />
          <Route path="/f/:formId" element={<PublicForm />} />
          <Route path="/m/:id" element={<PublicBooking />} />
          <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
          <Route path="/homecare-buying-help" element={<BuyingHelp />} />
          <Route path="/how-to-choose" element={<Navigate to="/homecare-buying-help" replace />} />
          <Route path="/homecare-buying-help/assessment/:token" element={<BuyingHelpSurvey />} />
          <Route path="/preferences/:token" element={<PreferenceCenter />} />
          <Route element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route path="/admin" element={<Dashboard />} />
            <Route path="/admin/pipeline" element={<Pipeline />} />
            <Route path="/admin/companies" element={<Companies />} />
            <Route path="/admin/care-directory" element={<CareDirectory />} />
            <Route path="/admin/market-intel" element={<MarketIntel />} />
            <Route path="/admin/events" element={<Events />} />
            <Route path="/admin/funnel" element={<FunnelInbox />} />
            <Route path="/admin/ops" element={<Ops />} />
            <Route path="/admin/curated-lists" element={<CuratedLists />} />
            <Route path="/admin/assurance" element={<AssurancePipeline />} />
            <Route path="/admin/verification" element={<VerificationAssurance />} />
            <Route path="/admin/procurement-reports" element={<ProcurementReports />} />
            <Route path="/admin/trust" element={<SupplierTrust />} />
            <Route path="/admin/listing-proposals" element={<ListingProposals />} />
            <Route path="/admin/governance" element={<Governance />} />
            <Route path="/admin/question-sets" element={<QuestionSets />} />
            <Route path="/admin/fulfilment" element={<Fulfilment />} />
            <Route path="/admin/partners" element={<Partners />} />
            <Route path="/admin/subscriptions" element={<VendorSubscriptions />} />
            <Route path="/admin/contacts" element={<Contacts />} />
            <Route path="/admin/lists" element={<Lists />} />
            <Route path="/admin/forms" element={<Forms />} />
            <Route path="/admin/meetings" element={<Meetings />} />
            <Route path="/admin/campaigns" element={<Campaigns />} />
            <Route path="/admin/bulk-email" element={<BulkEmail />} />
            <Route path="/admin/campaigns/:id" element={<CampaignBuilder />} />
            <Route path="/admin/analytics" element={<Analytics />} />
            <Route path="/admin/settings" element={<Settings />} />
            <Route path="/admin/custom-properties" element={<CustomProperties />} />
            <Route path="/admin/guide" element={<UserGuide />} />
            <Route path="/admin/automations" element={<AutomationsHub />} />
            <Route path="/admin/inbox" element={<Inbox />} />
            <Route path="/admin/listing-approvals" element={<ListingApprovals />} />
            <Route path="/admin/discount-codes" element={<DiscountCodes />} />
            <Route path="/admin/review-register" element={<ReviewRegisterAdmin />} />
            <Route path="/admin/product-reviews" element={<ProductReviews />} />
            <Route path="/admin/supplier-editor" element={<SupplierEditor />} />
            <Route path="/admin/supplier-verification" element={<SupplierVerification />} />
            <Route path="/admin/supplier-invites" element={<SupplierInvites />} />          </Route>
          <Route path="/claim/success" element={<ClaimSuccess />} />
          <Route path="/claim/set-password" element={<SetPassword />} />
          <Route path="/claim/:slug" element={<ClaimListing />} />
          <Route path="/portal" element={<SupplierPortal />} />
          <Route element={<PublicLayout />}>
            <Route path="/" element={<PublicHome />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/suppliers/:slug" element={<SupplierPage />} />
            <Route path="/products/:slug" element={<ProductPage />} />
            <Route path="/how-to-choose" element={<HowToChoose />} />
            <Route path="/homecare-webinar" element={<HomecareWebinar />} />
            <Route path="/homecare-questionnaire" element={<HomecareQuestionnaire />} />
            <Route path="/procurement" element={<ProcurementHub />} />
            <Route path="/procurement/report/success" element={<ProcurementReportSuccess />} />
            <Route path="/procurement/report/:token" element={<ProcurementReportView />} />
            <Route path="/procurement/insurance" element={<InsuranceValue />} />
            <Route path="/procurement/:slug/report" element={<ProcurementReportForm />} />
            <Route path="/procurement/:slug" element={<ProcurementFunnel />} />
            <Route path="/assurance" element={<AssuranceProgramme />} />
            <Route path="/assurance/success" element={<AssuranceSuccess />} />
            <Route path="/assurance/evidence/:token" element={<AssuranceEvidence />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/for-suppliers" element={<ForSuppliers />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/create-listing" element={<CreateListing />} />
            <Route path="/independently-reviewed" element={<ReviewRegister />} />
            <Route path="/create-listing/pay/:claimId" element={<CreateListingPay />} />
            <Route path="/create-listing/:token" element={<CreateListing />} />
            <Route path="/advisor" element={<Advisor />} />
            <Route path="/knowledge-hub" element={<KnowledgeHub />} />
            <Route path="/knowledge-hub/:slug" element={<ArticlePage />} />
            <Route path="/podcast" element={<Podcast />} />
            <Route path="/podcast/:slug" element={<PodcastPost />} />
            <Route path="/webinars" element={<Webinars />} />
            <Route path="/webinar/watch/:token" element={<WebinarWatch />} />
            <Route path="/book-a-call" element={<BookACall />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/for-government" element={<ForGovernment />} />
            <Route path="/for-membership-orgs" element={<ForMembershipOrgs />} />
            <Route path="/for-industry-specialists" element={<ForIndustrySpecialists />} />
            <Route path="/for-care-providers" element={<ForCareProviders />} />
            <Route path="/for-care-services" element={<ForCareServices />} />
            <Route path="/common-problems" element={<CommonProblems />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="/terms-of-business" element={<TermsOfBusiness />} />
            <Route path="/acceptable-use-policy" element={<AcceptableUse />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </OrgProvider>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;
